import React from 'react';

/**
 * Lead Score Gauge Component
 * Visual representation of lead score with circular or progress bar display
 */
const LeadScoreGauge = ({
    value = 0,
    size = 'md',  // 'sm', 'md', 'lg'
    showLabel = true,
    className = '',
}) => {
    // Normalize value to 0-100
    const score = Math.min(100, Math.max(0, value));

    // Determine color based on score
    const getColor = () => {
        if (score >= 70) return { text: 'text-green-400', bg: 'bg-green-400', ring: 'ring-green-400/20', glow: 'shadow-green-500/30' };
        if (score >= 50) return { text: 'text-yellow-400', bg: 'bg-yellow-400', ring: 'ring-yellow-400/20', glow: 'shadow-yellow-500/30' };
        return { text: 'text-red-400', bg: 'bg-red-400', ring: 'ring-red-400/20', glow: 'shadow-red-500/30' };
    };

    const colors = getColor();

    // Size configurations
    const sizes = {
        sm: { container: 'w-10 h-10', text: 'text-xs', label: 'text-[10px]' },
        md: { container: 'w-14 h-14', text: 'text-sm', label: 'text-xs' },
        lg: { container: 'w-20 h-20', text: 'text-lg', label: 'text-sm' },
    };

    const sizeConfig = sizes[size] || sizes.md;

    // Calculate stroke dash for circular gauge
    const circumference = 2 * Math.PI * 45; // r=45 for viewBox of 100
    const strokeDash = (score / 100) * circumference;

    return (
        <div className={`relative flex flex-col items-center ${className}`}>
            {/* Circular Gauge */}
            <div className={`relative ${sizeConfig.container}`}>
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Background circle */}
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="8"
                        className="text-gray-700"
                    />
                    {/* Progress circle */}
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference - strokeDash}
                        className={`${colors.text} transition-all duration-500 ease-out`}
                    />
                </svg>
                {/* Score number in center */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`font-bold ${colors.text} ${sizeConfig.text}`}>
                        {Math.round(score)}
                    </span>
                </div>
            </div>

            {/* Label */}
            {showLabel && (
                <span className={`mt-1 text-gray-400 ${sizeConfig.label}`}>Score</span>
            )}
        </div>
    );
};

/**
 * Alternative Progress Bar version of Lead Score
 */
export const LeadScoreBar = ({
    value = 0,
    showValue = true,
    showLabel = true,
    label = 'Lead Score',
    className = '',
}) => {
    const score = Math.min(100, Math.max(0, value));

    const getColor = () => {
        if (score >= 70) return 'bg-green-500';
        if (score >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <div className={`${className}`}>
            {showLabel && (
                <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-400">{label}</span>
                    {showValue && (
                        <span className="text-xs font-medium text-white">{Math.round(score)}%</span>
                    )}
                </div>
            )}
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                    className={`h-full ${getColor()} rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${score}%` }}
                />
            </div>
        </div>
    );
};

export default LeadScoreGauge;
